import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sparkles, ChevronDown, Instagram, Youtube, Twitch } from "lucide-react";
import { NAV, NAV_FOR_YOU, NAV_MORE, TOK_LOGO } from "@/lib/data";
import { TikTokIcon, TOK_SOCIAL_HANDLES } from "@/components/SocialFunnel";

const SOCIAL_ICONS = [
  { key: "tiktok", href: TOK_SOCIAL_HANDLES.tiktok, Icon: TikTokIcon, label: "TikTok" },
  { key: "instagram", href: TOK_SOCIAL_HANDLES.instagram, Icon: Instagram, label: "Instagram" },
  { key: "youtube", href: TOK_SOCIAL_HANDLES.youtube, Icon: Youtube, label: "YouTube" },
  { key: "twitch", href: TOK_SOCIAL_HANDLES.twitch, Icon: Twitch, label: "Twitch" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [forYouOpen, setForYouOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const moreRef = useRef(null);
  const forYouRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
    setForYouOpen(false);
  }, [location.pathname]);

  // Close 'More' / 'For You' on outside click + Escape
  useEffect(() => {
    if (!moreOpen && !forYouOpen) return;
    const onDocClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
      if (forYouRef.current && !forYouRef.current.contains(e.target)) {
        setForYouOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMoreOpen(false);
        setForYouOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen, forYouOpen]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-amber-500/10"
          : "bg-gradient-to-b from-black/70 via-black/30 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 h-[72px] flex items-center justify-between gap-8">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-4 group shrink-0"
        >
          <img
            src={TOK_LOGO}
            alt="Temple of Kulture"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-amber-400/40 group-hover:ring-amber-400 transition-all"
          />
          <span className="hidden sm:inline font-display font-black tracking-[0.22em] text-[11px] uppercase leading-tight">
            Temple of <br className="lg:hidden" />
            <span className="text-amber-400">Kulture</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7 flex-1 justify-center">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `tok-cta-label whitespace-nowrap transition-colors duration-300 ${
                  isActive
                    ? "text-amber-400"
                    : "text-zinc-300 hover:text-amber-400"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {/* For You dropdown — audience pathways */}
          <div className="relative" ref={forYouRef}>
            <button
              type="button"
              data-testid="nav-for-you-button"
              onClick={() => { setForYouOpen((v) => !v); setMoreOpen(false); }}
              aria-expanded={forYouOpen}
              aria-haspopup="true"
              className={`tok-cta-label inline-flex items-center gap-1.5 whitespace-nowrap transition-colors duration-300 ${
                forYouOpen ? "text-amber-400" : "text-zinc-300 hover:text-amber-400"
              }`}
            >
              For You
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-300 ${forYouOpen ? "rotate-180" : ""}`}
              />
            </button>

            {forYouOpen && (
              <div
                data-testid="nav-for-you-dropdown"
                className="absolute right-0 top-full mt-4 w-72 bg-black/95 backdrop-blur-2xl border border-amber-500/20 shadow-[0_20px_60px_rgba(0,0,0,0.6)] py-4"
              >
                <ul className="px-2">
                  {NAV_FOR_YOU.map((item) => (
                    <li key={item.label}>
                      <NavLink
                        to={item.to}
                        data-testid={`nav-for-you-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                        onClick={() => setForYouOpen(false)}
                        className={({ isActive }) =>
                          `block px-4 py-3 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
                            isActive
                              ? "text-amber-400 bg-amber-400/5"
                              : "text-zinc-300 hover:text-amber-400 hover:bg-white/[0.03]"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* More dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              data-testid="nav-more-button"
              onClick={() => { setMoreOpen((v) => !v); setForYouOpen(false); }}
              aria-expanded={moreOpen}
              aria-haspopup="true"
              className={`tok-cta-label inline-flex items-center gap-1.5 whitespace-nowrap transition-colors duration-300 ${
                moreOpen ? "text-amber-400" : "text-zinc-300 hover:text-amber-400"
              }`}
            >
              More
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`}
              />
            </button>

            {moreOpen && (
              <div
                data-testid="nav-more-dropdown"
                className="absolute right-0 top-full mt-4 w-72 bg-black/95 backdrop-blur-2xl border border-amber-500/20 shadow-[0_20px_60px_rgba(0,0,0,0.6)] py-4"
              >
                <ul className="px-2">
                  {NAV_MORE.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`nav-more-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                        onClick={() => setMoreOpen(false)}
                        className={({ isActive }) =>
                          `block px-4 py-3 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors ${
                            isActive
                              ? "text-amber-400 bg-amber-400/5"
                              : "text-zinc-300 hover:text-amber-400 hover:bg-white/[0.03]"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 mx-4 pt-4 border-t border-white/10">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400/80 font-bold mb-3">
                    Socials
                  </p>
                  <div className="flex items-center gap-2">
                    {SOCIAL_ICONS.map(({ key, href, Icon, label }) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Temple of Kulture on ${label}`}
                        data-testid={`nav-more-social-${key}`}
                        className="inline-flex h-9 w-9 items-center justify-center border border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300 transition-colors"
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/whats-on"
            data-testid="header-book-cta"
            className="hidden md:inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 transition-colors text-black px-5 py-2.5 tok-cta-label"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Book
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden inline-flex h-10 w-10 items-center justify-center text-white border border-white/15 hover:border-amber-400 hover:text-amber-400 transition-colors"
            aria-label="Open menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — full nav (primary + secondary) */}
      <div
        data-testid="mobile-nav"
        className={`xl:hidden overflow-hidden transition-[max-height,opacity] duration-500 bg-black/95 backdrop-blur-2xl border-t border-amber-500/10 ${
          open ? "max-h-[100vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 grid gap-1 overflow-y-auto max-h-[calc(100vh-72px)]">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `font-display uppercase tracking-[0.15em] text-xl py-3 border-b border-white/5 ${
                  isActive ? "text-amber-400" : "text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-amber-400/70 font-bold">
            For You
          </p>
          {NAV_FOR_YOU.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              data-testid={`mobile-nav-for-you-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `font-display uppercase tracking-[0.15em] text-base py-2.5 border-b border-white/5 ${
                  isActive ? "text-amber-400" : "text-zinc-300"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <p className="mt-6 text-[10px] uppercase tracking-[0.35em] text-amber-400/70 font-bold">
            More
          </p>
          {NAV_MORE.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-more-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
              className={({ isActive }) =>
                `font-display uppercase tracking-[0.15em] text-base py-2.5 border-b border-white/5 ${
                  isActive ? "text-amber-400" : "text-zinc-300"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="mt-4 flex items-center gap-2">
            {SOCIAL_ICONS.map(({ key, href, Icon, label }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Temple of Kulture on ${label}`}
                data-testid={`mobile-nav-social-${key}`}
                className="inline-flex h-10 w-10 items-center justify-center border border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300 transition-colors"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
          <Link
            to="/corporate"
            data-testid="mobile-corporate-cta"
            className="mt-6 bg-amber-400 text-black text-center py-4 font-bold uppercase tracking-[0.25em] text-xs"
          >
            Corporate Enquiry
          </Link>
        </div>
      </div>
    </header>
  );
}
